import { useState, useEffect } from 'react';
import { api } from '../api';
import type { Establishment } from '../types';

export default function Admin() {
  const [tab, setTab] = useState<'establishments' | 'telegram'>('establishments');
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [telegramUsers, setTelegramUsers] = useState<{ chat_id: number; username: string | null; first_name: string | null }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (tab === 'establishments') {
      loadEstablishments();
    } else {
      loadTelegramUsers();
    }
  }, [tab]);

  const loadEstablishments = async () => {
    try {
      setLoading(true);
      const data = await api.getEstablishments();
      setEstablishments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load establishments');
    } finally {
      setLoading(false);
    }
  };

  const loadTelegramUsers = async () => {
    try {
      setLoading(true);
      const data = await api.getTelegramCache();
      setTelegramUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load telegram cache');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEstablishment = async (id: number) => {
    try {
      await api.deleteEstablishment(id);
      loadEstablishments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete establishment');
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: '1rem' }}>Админ-панель</h2>

      <div className="tabs">
        <button className={`tab ${tab === 'establishments' ? 'active' : ''}`} onClick={() => setTab('establishments')}>
          Заведения
        </button>
        <button className={`tab ${tab === 'telegram' ? 'active' : ''}`} onClick={() => setTab('telegram')}>
          Telegram кэш
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : tab === 'establishments' ? (
        <EstablishmentsTab
          establishments={establishments}
          onCreateNew={() => setShowCreateModal(true)}
          onDelete={handleDeleteEstablishment}
        />
      ) : (
        <TelegramTab users={telegramUsers} />
      )}

      {showCreateModal && (
        <CreateEstablishmentModal
          onClose={() => setShowCreateModal(false)}
          onCreated={loadEstablishments}
        />
      )}
    </div>
  );
}

function EstablishmentsTab({
  establishments,
  onCreateNew,
  onDelete,
}: {
  establishments: Establishment[];
  onCreateNew: () => void;
  onDelete: (id: number) => void;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <span>Всего: {establishments.length}</span>
        <button className="btn btn-primary" onClick={onCreateNew}>
          + Создать
        </button>
      </div>

      {establishments.length === 0 ? (
        <div className="empty-state">
          <p>Нет заведений</p>
        </div>
      ) : (
        <div className="user-list">
          {establishments.map((est) => (
            <div key={est.id} className="user-item">
              <div className="user-info">
                <span className="user-name">{est.name}</span>
                <span className="user-role">{est.address || 'Без адреса'}</span>
              </div>
              <button
                className="btn btn-danger"
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                onClick={() => {
                  if (confirm(`Удалить заведение "${est.name}"?`)) {
                    onDelete(est.id);
                  }
                }}
              >
                Удалить
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TelegramTab({ users }: { users: { chat_id: number; username: string | null; first_name: string | null }[] }) {
  return (
    <div>
      <p style={{ marginBottom: '1rem', color: 'var(--gray-500)' }}>
        Пользователи, которые написали боту /start
      </p>

      {users.length === 0 ? (
        <div className="empty-state">
          <p>Нет данных</p>
        </div>
      ) : (
        <div className="user-list">
          {users.map((u) => (
            <div key={u.chat_id} className="user-item">
              <div className="user-info">
                <span className="user-name">
                  {u.first_name || 'Unknown'}
                  {u.username && <span style={{ color: 'var(--gray-500)' }}> @{u.username}</span>}
                </span>
                <span className="user-role">ID: {u.chat_id}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateEstablishmentModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;

    setLoading(true);
    setError(null);

    try {
      await api.createEstablishment({ name, address: address || undefined });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create establishment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Создать заведение</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Название *</label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Адрес</label>
            <input
              type="text"
              className="form-input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading || !name}>
            {loading ? 'Создание...' : 'Создать'}
          </button>
        </form>
      </div>
    </div>
  );
}
